import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Camera, Clock, Package, Check, X, Search, Box, Home, Archive, HelpCircle, ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import endlessStorageLogo from "@/assets/endless-storage-logo.png";
import { CameraCapture } from "@/components/CameraCapture";

export default function BoxAnalyzer() {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [email, setEmail] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [boxNumber, setBoxNumber] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check for boxNumber query parameter from QR code scan
    const qrBoxNumber = searchParams.get('boxNumber');
    if (qrBoxNumber) {
      setBoxNumber(qrBoxNumber);
    }
  }, [searchParams]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
  };

  const handleCameraCapture = (imageData: string) => {
    // Convert base64 to File object
    fetch(imageData)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], "camera-capture.jpg", { type: "image/jpeg" });
        setSelectedFile(file);
      });
  };

  const handleSubmit = async () => {
    if (!selectedFile || !email) {
      toast({
        title: "Missing Information",
        description: "Please provide both a photo and email address.",
        variant: "destructive",
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzing(true);
    setUploadProgress(30);

    try {
      // Capture UTM parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      
      // Capture attribution data
      const attribution = {
        utm_source: urlParams.get('utm_source') || undefined,
        utm_medium: urlParams.get('utm_medium') || undefined,
        utm_campaign: urlParams.get('utm_campaign') || undefined,
        utm_term: urlParams.get('utm_term') || undefined,
        utm_content: urlParams.get('utm_content') || undefined,
        referrer_url: document.referrer || undefined,
        landing_page: window.location.href,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      // Convert to base64
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      
      reader.onload = async () => {
        try {
          const base64Image = reader.result as string;
          setUploadProgress(50);

          console.time('analyzeInvoke');

          // 45s client-side timeout fallback
          const timeoutId = setTimeout(() => {
            console.warn('[CLIENT] 45s timeout reached');
            setIsAnalyzing(false);
            setUploadProgress(0);
            toast({
              title: "Taking Longer Than Usual",
              description: "This is taking longer than expected. Please try again.",
              variant: "destructive",
            });
          }, 45000);

          // Call edge function to analyze with email, attribution, and box number
          const { data, error } = await supabase.functions.invoke('analyze-box-anonymous', {
            body: { 
              imageData: base64Image,
              email: email.trim(),
              attribution,
              boxNumber: boxNumber || undefined
            }
          });

          clearTimeout(timeoutId);
          console.timeEnd('analyzeInvoke');
          console.log('Analyze response', data);

          if (error) {
            throw new Error(error.message || 'Failed to analyze image');
          }

          // Support both sessionId (camelCase) and session_id (snake_case)
          const sessionId = (data as any)?.sessionId ?? (data as any)?.session_id;
          
          if (!sessionId) {
            console.error('No session ID in response:', data);
            throw new Error('No session ID returned from analysis');
          }

          setUploadProgress(100);

          // Navigate to results page
          setTimeout(() => {
            navigate(`/try/results/${sessionId}`);
          }, 500);
        } catch (error: any) {
          console.error('Analysis error in reader:', error);
          setIsAnalyzing(false);
          setUploadProgress(0);
          toast({
            title: "Analysis Failed",
            description: error.message || "Failed to analyze your box. Please try again.",
            variant: "destructive",
          });
        }
      };

      reader.onerror = () => {
        console.error('File reader error');
        setIsAnalyzing(false);
        setUploadProgress(0);
        toast({
          title: "File Error",
          description: "Failed to read image file. Please try again.",
          variant: "destructive",
        });
      };

    } catch (error: any) {
      console.error('Analysis error:', error);
      toast({
        title: "Analysis Failed",
        description: error.message || "Failed to analyze your box. Please try again.",
        variant: "destructive",
      });
      setIsAnalyzing(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a 
              href="https://endless-storage.com"
              className="hover:opacity-80 transition-opacity"
            >
              <img src={endlessStorageLogo} alt="Endless Storage" className="h-10" />
            </a>
            <Button 
              variant="outline" 
              onClick={() => navigate('/auth')}
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
            <Package className="h-4 w-4" />
            <span className="text-sm font-medium">Free Moving Inventory Tool</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            Moving? Track What's In Every Box
          </h1>

          {boxNumber && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/20 text-primary mb-4">
              <span className="text-sm font-medium">Box Number: {boxNumber}</span>
            </div>
          )}

          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Snap a photo of each box as you pack. Get an instant inventory so you can find anything during your move—without opening 20 boxes.
          </p>

          {/* Upload Zone */}
          <Card className="p-12 border-2 border-dashed border-primary/20 hover:border-primary/40 transition-colors">
            {isAnalyzing ? (
              <div className="space-y-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <Package className="h-8 w-8 text-primary animate-pulse" />
                </div>
                <h3 className="text-xl font-semibold">Analyzing your box...</h3>
                <p className="text-muted-foreground">Identifying items and creating your inventory</p>
                <div className="w-full max-w-xs mx-auto bg-muted rounded-full h-2 overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  💡 Did you know? The average NYC apartment has 2,000+ items!
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-4">
                  <Camera className="h-10 w-10 text-primary" />
                </div>
                
                <div>
                  <h3 className="text-2xl font-semibold mb-2">Get Your Box Inventory</h3>
                  <p className="text-muted-foreground">Take a photo, enter your email, and see what's inside in 15 seconds</p>
                </div>

                <div className="max-w-md mx-auto space-y-4 text-left">
                  {/* Photo Upload */}
                  <div>
                    <Label htmlFor="photo-upload" className="text-base">
                      📸 Box Photo
                    </Label>
                    <div className="flex gap-2 mt-2">
                      <Button 
                        variant="outline" 
                        className="flex-1" 
                        onClick={() => setCameraOpen(true)}
                      >
                        <Camera className="mr-2 h-5 w-5" />
                        Take Photo
                      </Button>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="photo-upload"
                      />
                      <label htmlFor="photo-upload" className="flex-1">
                        <Button 
                          variant="outline" 
                          className="w-full cursor-pointer" 
                          asChild
                        >
                          <span>
                            <Upload className="mr-2 h-5 w-5" />
                            Upload
                          </span>
                        </Button>
                      </label>
                    </div>
                    {selectedFile && (
                      <p className="text-xs text-muted-foreground mt-1">
                        ✓ {selectedFile.name}
                      </p>
                    )}
                  </div>

                  {/* Email Input */}
                  <div>
                    <Label htmlFor="email" className="text-base">
                      📧 Your Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      We'll send your box inventory + free moving tracker app
                    </p>
                  </div>

                  {/* Submit Button */}
                  <Button 
                    size="lg" 
                    className="w-full"
                    onClick={handleSubmit}
                    disabled={!selectedFile || !email}
                  >
                    <Package className="mr-2 h-5 w-5" />
                    Get My Box Inventory Free
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-8">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Check className="h-6 w-6 text-primary" />
                    </div>
                    <p className="text-sm font-medium">Instant item recognition</p>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Clock className="h-6 w-6 text-primary" />
                    </div>
                    <p className="text-sm font-medium">Results in 15 seconds</p>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Package className="h-6 w-6 text-primary" />
                    </div>
                    <p className="text-sm font-medium">Free moving tracker included</p>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* How It Works */}
        <div className="max-w-4xl mx-auto mt-24">
          <h2 className="text-3xl font-bold text-center mb-4">From Chaos to Organized in 3 Steps</h2>
          <p className="text-center text-muted-foreground mb-12">Stop playing "what's in this box?" on moving day</p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-6xl mb-4">📸</div>
              <h3 className="text-xl font-semibold mb-2">Snap Before Sealing</h3>
              <p className="text-muted-foreground">Take a photo of what's inside each box before you tape it shut</p>
            </div>

            <div className="text-center">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-xl font-semibold mb-2">Instant Inventory Created</h3>
              <p className="text-muted-foreground">We identify every item and create an itemized list for that box</p>
            </div>

            <div className="text-center">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold mb-2">Find Anything Instantly</h3>
              <p className="text-muted-foreground">Moving day: Search "coffee maker" and know exactly which box to open</p>
            </div>
          </div>
        </div>

        {/* Value Proposition */}
        <div className="max-w-4xl mx-auto mt-24">
          <h2 className="text-3xl font-bold text-center mb-12">Never Play "What's In This Box?" Again</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6">
              <div className="text-5xl mb-4">📦</div>
              <h3 className="text-lg font-semibold mb-2">Stop Opening Every Box</h3>
              <p className="text-muted-foreground">
                Search "phone charger" on move-in day and find it in Box #7—not Box #1 through #19
              </p>
            </Card>

            <Card className="p-6">
              <div className="text-5xl mb-4">⏱️</div>
              <h3 className="text-lg font-semibold mb-2">Unpack What You Need First</h3>
              <p className="text-muted-foreground">
                Know which boxes have your essentials vs. stuff that can wait
              </p>
            </Card>

            <Card className="p-6">
              <div className="text-5xl mb-4">🧠</div>
              <h3 className="text-lg font-semibold mb-2">Remember What You Packed</h3>
              <p className="text-muted-foreground">
                Three weeks later when you need something, search your inventory and find it instantly
              </p>
            </Card>
          </div>
        </div>

        {/* Social Proof */}
        <div className="max-w-4xl mx-auto mt-24">
          <h2 className="text-3xl font-bold text-center mb-2">Used By 1,000+ NYC Movers</h2>
          <p className="text-center text-muted-foreground mb-12">Real stories from people who tracked their moves</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6">
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-500">★</span>
                ))}
              </div>
              <p className="text-sm italic mb-4">
                "I took photos of 23 boxes as I packed. On moving day I searched 'winter coat' and found it immediately. Saved me hours."
              </p>
              <p className="text-xs text-muted-foreground">— Sarah M., Brooklyn</p>
            </Card>

            <Card className="p-6">
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-500">★</span>
                ))}
              </div>
              <p className="text-sm italic mb-4">
                "Usually I write 'misc' on everything. This actually told me what was in each box. Game changer."
              </p>
              <p className="text-xs text-muted-foreground">— Mike T., Upper East Side</p>
            </Card>

            <Card className="p-6">
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-500">★</span>
                ))}
              </div>
              <p className="text-sm italic mb-4">
                "Best part: I kept boxes in storage and could still search what was in them without driving to the facility."
              </p>
              <p className="text-xs text-muted-foreground">— Jennifer L., Manhattan</p>
            </Card>
          </div>
        </div>

        {/* Statistics */}
        <div className="max-w-4xl mx-auto mt-24">
          <h2 className="text-3xl font-bold text-center mb-4">The Average NYC Move Involves 30-50 Boxes</h2>
          <p className="text-center text-muted-foreground mb-12">Here's what that means without tracking</p>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 bg-destructive/5 border-destructive/20">
              <h3 className="text-xl font-semibold mb-4 text-destructive flex items-center gap-2">
                <X className="h-5 w-5" />
                Without Box Tracking
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <X className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                  <span className="text-sm">Open 10-15 boxes to find one item</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                  <span className="text-sm">Forget what you packed where</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                  <span className="text-sm">Unpack randomly, not strategically</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                  <span className="text-sm">Labels like "kitchen stuff" aren't helpful</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8 bg-primary/5 border-primary/20">
              <h3 className="text-xl font-semibold mb-4 text-primary flex items-center gap-2">
                <Check className="h-5 w-5" />
                With Photo Inventory
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-sm">Search and find in seconds</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-sm">Complete inventory of every box</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-sm">Unpack essentials first</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-sm">Know exactly what's in "Box #7"</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>

        {/* Feature Callouts */}
        <div className="max-w-4xl mx-auto mt-24">
          <Card className="p-8 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <h2 className="text-2xl font-bold text-center mb-8">What You Get (Free)</h2>
            
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
              <div className="flex items-center gap-3">
                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                <span>Instant item recognition</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                <span>Itemized list for each box</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                <span>Searchable inventory across all boxes</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                <span>Free moving tracker app</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                <span>Keep your inventory forever</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                <span>No credit card required</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Use Cases */}
        <div className="max-w-4xl mx-auto mt-24">
          <h2 className="text-3xl font-bold text-center mb-12">Not Just for Moving</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-6xl mb-4">📦</div>
              <h3 className="text-lg font-semibold mb-2">Moving</h3>
              <p className="text-sm text-muted-foreground">
                Track boxes from pack to unpack. Find anything without opening everything.
              </p>
            </Card>

            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-6xl mb-4">🏠</div>
              <h3 className="text-lg font-semibold mb-2">Storage</h3>
              <p className="text-sm text-muted-foreground">
                Know what's in your storage unit without driving there. Perfect for seasonal items.
              </p>
            </Card>

            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-6xl mb-4">🧹</div>
              <h3 className="text-lg font-semibold mb-2">Decluttering</h3>
              <p className="text-sm text-muted-foreground">
                Pack away things you don't use daily. Search your inventory when you need them.
              </p>
            </Card>
          </div>
        </div>

        {/* Secondary CTA */}
        <div className="max-w-2xl mx-auto mt-24">
          <Card className="p-12 border-2 border-primary/20 bg-gradient-to-br from-background to-primary/5">
            <h2 className="text-3xl font-bold text-center mb-4">Start Tracking Your First Box</h2>
            <p className="text-center text-muted-foreground mb-8">
              Upload a photo now. Get your itemized list in 15 seconds.
            </p>
            
            <div className="max-w-md mx-auto space-y-4">
              {/* Photo Upload */}
              <div>
                <Label htmlFor="photo-upload-2" className="text-base">
                  📸 Box Photo
                </Label>
                <div className="flex gap-2 mt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1" 
                    onClick={() => setCameraOpen(true)}
                  >
                    <Camera className="mr-2 h-5 w-5" />
                    Take Photo
                  </Button>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="photo-upload-2"
                  />
                  <label htmlFor="photo-upload-2" className="flex-1">
                    <Button 
                      variant="outline" 
                      className="w-full cursor-pointer" 
                      asChild
                    >
                      <span>
                        <Upload className="mr-2 h-5 w-5" />
                        Upload
                      </span>
                    </Button>
                  </label>
                </div>
                {selectedFile && (
                  <p className="text-xs text-muted-foreground mt-1">
                    ✓ {selectedFile.name}
                  </p>
                )}
              </div>

              {/* Email Input */}
              <div>
                <Label htmlFor="email-2" className="text-base">
                  📧 Your Email
                </Label>
                <Input
                  id="email-2"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2"
                />
              </div>

              {/* Submit Button */}
              <Button 
                size="lg" 
                className="w-full"
                onClick={handleSubmit}
                disabled={!selectedFile || !email}
              >
                <Package className="mr-2 h-5 w-5" />
                Get My Box Inventory Free
              </Button>
            </div>
            
            <p className="text-xs text-center text-muted-foreground mt-6">
              After you see your results, we'll send you the free moving tracker app to organize all your boxes in one place.
            </p>
          </Card>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto mt-24">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            <Card className="p-6">
              <h3 className="font-semibold mb-2 flex items-start gap-2">
                <HelpCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                Is this really free?
              </h3>
              <p className="text-sm text-muted-foreground ml-7">
                Yes. Analyze unlimited boxes, keep your inventory forever. No credit card required.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-2 flex items-start gap-2">
                <HelpCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                What happens to my photos?
              </h3>
              <p className="text-sm text-muted-foreground ml-7">
                We analyze them to create your inventory, then you choose whether to save them. Nothing is stored without your permission.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-2 flex items-start gap-2">
                <HelpCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                Do I need to download an app?
              </h3>
              <p className="text-sm text-muted-foreground ml-7">
                Not to try it. Upload a photo right here on the web. After you see your results, we'll send you the free app to track all your boxes during your move.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-2 flex items-start gap-2">
                <HelpCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                How accurate is the recognition?
              </h3>
              <p className="text-sm text-muted-foreground ml-7">
                Very accurate for common household items. It identifies furniture, electronics, clothes, kitchen items, books, and more.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-2 flex items-start gap-2">
                <HelpCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                What if I'm not moving right now?
              </h3>
              <p className="text-sm text-muted-foreground ml-7">
                Use it to inventory your storage unit, attic, or garage. Know what's stored away without digging through everything.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-2 flex items-start gap-2">
                <HelpCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                Can I track multiple boxes?
              </h3>
              <p className="text-sm text-muted-foreground ml-7">
                Yes. After your first box, you can sign up for the free app to track unlimited boxes and search across all of them.
              </p>
            </Card>
          </div>
        </div>

        {/* Storage Bridge */}
        <div className="max-w-3xl mx-auto mt-24 mb-16">
          <Card className="p-8 bg-muted/50 border-dashed border-2 hover:border-primary/40 transition-colors">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Archive className="h-6 w-6 text-primary" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">Moving but not ready to unpack everything?</h3>
                <p className="text-muted-foreground mb-4">
                  Some boxes can wait. Store them with Endless Storage for $9.99/box/month. We pick up, store safely, and deliver back in 48 hours when you need them.
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/auth')}
                  className="group"
                >
                  Learn About Storage
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Camera Modal */}
      <CameraCapture 
        open={cameraOpen} 
        onClose={() => setCameraOpen(false)} 
        onCapture={handleCameraCapture}
      />
    </div>
  );
}
